import { useEffect, useState } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { validateStagePosition } from "../api/validateStagePosition/validateStagePositionApi";

export type PositionCheckStatus = "idle" | "loading" | "valid" | "invalid";

interface UseStagePositionAvailabilityResult {
  status: PositionCheckStatus;
  message: string;
}

/**
 * Debounced availability check for a stage position, mirroring the voucher-name pattern.
 * Skips the check when the position is empty/invalid or unchanged from the stage's own
 * position (on edit), so keeping the current position never reads as "taken".
 */
export const useStagePositionAvailability = (
  position: number | null,
  originalPosition?: number,
): UseStagePositionAvailabilityResult => {
  const [status, setStatus] = useState<PositionCheckStatus>("idle");
  const [message, setMessage] = useState("");
  const debouncedPosition = useDebouncedValue(position, 500);

  useEffect(() => {
    if (
      debouncedPosition === null ||
      Number.isNaN(debouncedPosition) ||
      debouncedPosition < 1 ||
      debouncedPosition === originalPosition
    ) {
      setStatus("idle");
      setMessage("");
      return;
    }

    setStatus("loading");
    validateStagePosition({ position: debouncedPosition })
      .then((res) => {
        if (res.data.isAvailable) {
          setStatus("valid");
          setMessage(res.data.message || "Position is available!");
        } else {
          setStatus("invalid");
          setMessage(res.data.message || "Position is already in use.");
        }
      })
      .catch(() => {
        setStatus("invalid");
        setMessage("Could not validate position. Please try another.");
      });
  }, [debouncedPosition, originalPosition]);

  return { status, message };
};
